import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { QrCode, Loader2, CheckCircle2, Clock3 } from "lucide-react";
import { http, rupiah, apiError } from "@/lib/api";
import ThemeScope from "@/components/ThemeScope";
import { PRESETS } from "@/lib/themes";

export default function SimulatePay() {
  const { orderId } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [tokens, setTokens] = useState(PRESETS.midnight_noodle.tokens);
  const [paying, setPaying] = useState(false);
  const poll = useRef(null);

  async function fetchOrder() {
    try { const { data } = await http.get(`/orders/${orderId}`); setOrder(data); return data; }
    catch (e) { setOrder({ error: apiError(e) }); return null; }
  }

  useEffect(() => {
    http.get("/theme/public").then(({ data }) => setTokens(data.customer_theme?.tokens || tokens)).catch(() => {});
    fetchOrder();
  }, [orderId]);

  // Poll only for real Midtrans orders still pending
  useEffect(() => {
    if (order && order.midtrans && order.payment_status !== "paid") {
      poll.current = setInterval(async () => { const o = await fetchOrder(); if (o?.payment_status === "paid") clearInterval(poll.current); }, 4000);
      return () => clearInterval(poll.current);
    }
  }, [order?.midtrans, order?.payment_status]);

  async function pay() {
    setPaying(true);
    try { await http.post(`/orders/${orderId}/simulate-pay`); await fetchOrder(); }
    finally { setPaying(false); }
  }

  const paid = order && order.payment_status === "paid";
  const isReal = order && order.midtrans;

  return (
    <ThemeScope tokens={tokens} className="min-h-dvh">
      <div className="phone-frame flex min-h-dvh flex-col items-center justify-center p-6 text-center">
        <span className="text-xs font-bold uppercase tracking-[0.25em] text-primary">Pembayaran QRIS</span>
        {!order && <Loader2 className="wm-spin mt-6 text-muted-foreground" />}
        {order?.error && <p className="mt-6 text-muted-foreground">{order.error}</p>}

        {order && !order.error && !paid && (
          <div className="mt-6 w-full max-w-sm rounded-3xl border border-border bg-card p-6">
            <div className="mx-auto grid h-40 w-40 place-items-center rounded-2xl bg-muted"><QrCode size={96} className="text-card-foreground" /></div>
            <div className="mt-5 font-heading text-lg font-bold text-card-foreground">{order.id}</div>
            <div className="mt-1 font-heading text-3xl font-extrabold text-primary">{rupiah(order.total)}</div>
            {isReal ? (
              <div className="mt-4 flex items-center justify-center gap-2 text-sm text-muted-foreground"><Loader2 className="wm-spin" size={16} /> Menunggu konfirmasi pembayaran…</div>
            ) : (
              <>
                <p className="mt-3 text-xs text-muted-foreground">Mode simulasi (Midtrans belum dikonfigurasi). Klik untuk menandai lunas.</p>
                <button data-testid="simulate-pay-btn" onClick={pay} disabled={paying} className="mt-5 flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-3.5 font-bold text-primary-foreground transition-transform active:scale-95">
                  {paying ? <Loader2 className="wm-spin" size={18} /> : "Saya Sudah Bayar"}
                </button>
              </>
            )}
          </div>
        )}

        {paid && (
          <div className="mt-6 w-full max-w-sm rounded-3xl border border-border bg-card p-8">
            <CheckCircle2 className="mx-auto text-primary" size={64} />
            <div className="mt-4 font-heading text-2xl font-extrabold text-card-foreground">Pembayaran Berhasil</div>
            <p className="mt-2 text-sm text-muted-foreground">Pesananmu sedang disiapkan. Terima kasih!</p>
            <button onClick={() => nav("/history")} className="mt-5 w-full rounded-2xl bg-primary py-3.5 font-bold text-primary-foreground">Lihat Pesanan</button>
          </div>
        )}
      </div>
    </ThemeScope>
  );
}
